import { PRINT_HEADER_ID } from './modal-print.util';

export const QUALITY_RECORD_HEADER_CLASS = 'quality-record-header';
export const QUALITY_RECORD_HEADER_LOGO_CLASS = 'quality-record-header__logo';
export const QUALITY_RECORD_HEADER_LABEL_CLASS = 'quality-record-header__label';
export const QUALITY_RECORD_HEADER_CODE_CLASS = 'quality-record-header__code';
export const QUALITY_RECORD_HEADER_REVISION_CLASS = 'quality-record-header__revision';
export const QUALITY_RECORD_HEADER_TITLE_CLASS = 'quality-record-header__title';

const TITLE_LONG_THRESHOLD = 30;
const TITLE_XLONG_THRESHOLD = 45;

export type QualityRecordHeaderTitleFontSize = 'default' | 'long' | 'xlong';

export function resolveQualityRecordHeaderTitleFontSize(
  title: string
): QualityRecordHeaderTitleFontSize {
  if (title.length > TITLE_XLONG_THRESHOLD) return 'xlong';
  if (title.length > TITLE_LONG_THRESHOLD) return 'long';
  return 'default';
}

export function qualityRecordHeaderTitleClass(title: string): string {
  const size = resolveQualityRecordHeaderTitleFontSize(title);
  return size === 'default'
    ? QUALITY_RECORD_HEADER_TITLE_CLASS
    : `${QUALITY_RECORD_HEADER_TITLE_CLASS} ${QUALITY_RECORD_HEADER_TITLE_CLASS}--${size}`;
}

/**
 * Bare CSS (no `<style>` wrapper) so it can be embedded either directly inside a
 * `<style>` tag (`buildQualityRecordHeaderPrintStyleOverrides`) or concatenated
 * with another bare-CSS override string before a single wrapping tag is applied,
 * as `PrintButton`'s `printStyles` prop does — nesting `<style>` tags breaks HTML
 * parsing, since the first `</style>` encountered closes the outer element.
 *
 * `ModalDocumentInfo.vue` keeps its own fixed id (`#document-info-header`), which
 * `COMPACT_PRINT_STYLE` and `REPEATED_PRINT_HEADER_STYLE` in modal-print.util.ts
 * also target with `!important`. Matching `#document-info-header`'s own
 * specificity was not enough: `REPEATED_PRINT_HEADER_STYLE` selects with two ids
 * (e.g. `#htm-print-header #document-info-header td`), which beats a single-id
 * selector regardless of source order. The losing rules below are qualified with
 * both the `PRINT_HEADER_ID` wrapper id and the `quality-record-header` class on
 * the same compound selector (e.g.
 * `#${PRINT_HEADER_ID} #document-info-header.${QUALITY_RECORD_HEADER_CLASS} td`)
 * so they carry two ids plus a class, guaranteeing a specificity win over
 * `REPEATED_PRINT_HEADER_STYLE`'s two ids no matter where either block lands in
 * the concatenated `<style>` order. They're inert (never match) for
 * `RecordReadModal`/Lista Mestra, which use a different id and never combine
 * with `compact`.
 *
 * No `overflow`/`white-space`/`text-overflow` truncation on the title: a real
 * quality record's name is never cut, only shrunk in font-size steps — an
 * extreme-length title is allowed to grow the cell instead.
 *
 * `border` here is explicit and in `pt`, not left to the markup's Tailwind
 * `border` utility (1px): once the table is forced onto exact `cm` widths, a
 * `px` hairline lands at a fractional coordinate once the browser's print
 * pipeline converts it — that sub-pixel offset renders as a near-invisible
 * line in print/PDF output (reported visually against a real generated PDF in
 * Chrome's own PDF.js viewer, not just a rendering assumption). `pt` matches
 * the print pipeline's native unit. The table element also gets its own
 * `border`, not just the cells: with `border-collapse: collapse`, only the
 * interior lines two neighboring cells agree on render reliably — the outer
 * perimeter (top/left edge) has no neighbor to collapse with, and that's
 * exactly the edge that vanished; declaring it on the table itself covers it
 * independently of the per-cell borders. Same fix as the backend's
 * `quality-record-header.util.ts`.
 */
export const QUALITY_RECORD_HEADER_PRINT_STYLE_RULES = `
  table.${QUALITY_RECORD_HEADER_CLASS} {
    table-layout: fixed !important;
    width: 17cm !important;
    border-collapse: collapse !important;
    border: 1pt solid #231f20 !important;
  }
  .${QUALITY_RECORD_HEADER_CLASS} td {
    font-family: Arial, 'Liberation Sans', Helvetica, sans-serif !important;
    font-weight: 700 !important;
    font-size: 12pt !important;
    text-align: center !important;
    vertical-align: middle !important;
    color: #000 !important;
    line-height: 1.1 !important;
    padding: 0.05cm 0.1cm !important;
    height: 1cm !important;
    border: 1pt solid #231f20 !important;
  }
  .${QUALITY_RECORD_HEADER_LOGO_CLASS} { width: 4cm !important; }
  .${QUALITY_RECORD_HEADER_LOGO_CLASS} img,
  .${QUALITY_RECORD_HEADER_LOGO_CLASS} svg {
    max-width: 3.4cm !important;
    max-height: 1.6cm !important;
  }
  .${QUALITY_RECORD_HEADER_LABEL_CLASS} { width: 7cm !important; }
  .${QUALITY_RECORD_HEADER_CODE_CLASS} { width: 3cm !important; }
  .${QUALITY_RECORD_HEADER_REVISION_CLASS} { width: 3cm !important; }
  .${QUALITY_RECORD_HEADER_TITLE_CLASS} {
    text-transform: uppercase !important;
  }
  .${QUALITY_RECORD_HEADER_TITLE_CLASS}--long { font-size: 10pt !important; }
  .${QUALITY_RECORD_HEADER_TITLE_CLASS}--xlong { font-size: 8.5pt !important; }

  #${PRINT_HEADER_ID} #document-info-header.${QUALITY_RECORD_HEADER_CLASS} td {
    font-family: Arial, 'Liberation Sans', Helvetica, sans-serif !important;
    font-weight: 700 !important;
    font-size: 12pt !important;
    line-height: 1.1 !important;
    padding: 0.05cm 0.1cm !important;
    height: 1cm !important;
    border: 1pt solid #231f20 !important;
  }
  #document-info-header td[rowspan].${QUALITY_RECORD_HEADER_LOGO_CLASS} { width: 4cm !important; }
  #document-info-header .text-lg.${QUALITY_RECORD_HEADER_TITLE_CLASS} {
    font-size: 12pt !important;
    line-height: 1.1 !important;
  }
  #document-info-header .${QUALITY_RECORD_HEADER_LABEL_CLASS} .text-lg {
    font-size: 12pt !important;
    line-height: 1.1 !important;
  }
  #document-info-header .text-lg.${QUALITY_RECORD_HEADER_TITLE_CLASS}--long {
    font-size: 10pt !important;
  }
  #document-info-header .text-lg.${QUALITY_RECORD_HEADER_TITLE_CLASS}--xlong {
    font-size: 8.5pt !important;
  }
  #document-info-header .${QUALITY_RECORD_HEADER_CODE_CLASS} .text-sm,
  #document-info-header .${QUALITY_RECORD_HEADER_REVISION_CLASS} .text-sm {
    font-size: 12pt !important;
    line-height: 1.1 !important;
  }
  #${PRINT_HEADER_ID} #document-info-header.${QUALITY_RECORD_HEADER_CLASS} svg,
  #${PRINT_HEADER_ID} #document-info-header.${QUALITY_RECORD_HEADER_CLASS} img {
    max-width: 3.4cm !important;
    max-height: 1.6cm !important;
    width: auto !important;
    height: auto !important;
  }
`;

export function buildQualityRecordHeaderPrintStyleOverrides(): string {
  return `<style>${QUALITY_RECORD_HEADER_PRINT_STYLE_RULES}</style>`;
}
